import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SlideAnimationProvider } from "contexts/SlideAnimation";
import { SplashPage } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AddOnItem,
  AddOnItemName,
  BentoCategory,
  OrganizationNiche,
  OrganizationNicheStatus,
  SearchParams,
  TemplateIntakeForm,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import { BENTO_VA_ONBOARDING_CALENDLY_LINK } from "constants/influencer";
import CopywritersFlow from "features/Auth/SelfServeVaOnboarding/TemplatesPick/CopywritersFlow";
import { getQuantityForItem } from "features/Auth/SelfServeVaOnboarding/helpers";
import { fetcherAuth } from "utils/api";
import { identifyUser, trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import NicheForm from "./NicheForm";
import NicheTemplateForm from "./NicheTemplateForm";
import styles from "./styles";

enum Screen {
  niche = "niche",
  niche_templates = "niche_templates",
  template = "template",
  completed = "completed",
}

const VaBundledIntakeForm = () => {
  const navigate = useNavigate();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const {
    currentOrg,
    setCurrentOrg,
    setProfile,
    organizationVaProfile,
    setOrganizationVaProfile,
  } = useContext(OrganizationUserContext);
  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);
  const [searchParams] = useSearchParams();
  const [screen, setScreen] = useState<Screen | null>(null);

  const invoiceId = searchParams.get(SearchParams.INVOICE_ID);
  const [loading, setLoading] = useState(true);
  const [addOnItems, setAddOnItems] = useState<AddOnItem[]>([]);
  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);
  const [niches, setNiches] = useState<OrganizationNiche[]>([]);
  const [templateIntakeForms, setTemplateIntakeForms] = useState<
    TemplateIntakeForm[]
  >([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const numOfNiche =
    getQuantityForItem(AddOnItemName.niche, addOnItems || []) || 0;
  const numOfCall =
    getQuantityForItem(AddOnItemName.support_call, addOnItems) || 0;
  const numOfTemplates =
    getQuantityForItem(AddOnItemName.pitching_templates, addOnItems) || 0;
  const templateAddOn = addOnItems.find(
    (x) => x.itemName === AddOnItemName.pitching_templates,
  );

  const handleBookCall = () => {
    trackEvent("VA Bundled Page - Book Call Button Clicked");
  };

  const goToDashboard = () => {
    navigate(`/${routes.home}`);
  };

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      identifyUser(res.organizationUser.id);
      setCurrentOrg(res.organization);
      setOrganizationVaProfile(res.organizationVaProfile);
      setProfile(res.organizationProfile);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchasedItems = async () => {
    try {
      setLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/invoice/${invoiceId}`,
        "GET",
      );
      const { niches } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches`,
      );
      setOrganizationVaProfile((prev) => {
        const copy = makeDeepCopy(prev || {});
        copy.niches = niches;
        return copy;
      });
      const templateIntakeForms = res.addOnItems.find(
        (item: AddOnItem) =>
          item?.templateIntakeForms && item?.templateIntakeForms?.length > 0,
      )?.templateIntakeForms;
      if (templateIntakeForms) {
        setTemplateIntakeForms(templateIntakeForms);
      }
      setAddOnItems(res.addOnItems);
      redirectToRightPage(res.addOnItems, niches);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoriesToPickFrom = async () => {
    try {
      setLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding?for_va=${true}`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitNiche = async (e: CustomEvent) => {
    e.preventDefault();
    const noBentoCategoryIds = niches.filter((niche) => !niche.bentoCategoryId);
    if (noBentoCategoryIds.length > 0) {
      setAlert("Please select a category for each niche", "error");
      return;
    }
    if (templateIntakeForms && templateIntakeForms?.length > 0) {
      const needsTemplate = niches.filter(
        (niche) => niche.needsTemplate,
      ).length;
      if (needsTemplate !== templateIntakeForms.length) {
        setAlert(
          `You paid for ${templateIntakeForms?.length} templates. Please select ${templateIntakeForms.length} niches you need to write this template for`,
          "warning",
        );
        return;
      }
    }

    setSaveLoading(true);
    try {
      const { niches: updatedNiches } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/bulk-upsert`,
        "POST",
        {},
        {
          niches,
          source: SplashPage.va_bundled,
          addOnItemId: templateAddOn?.id,
        },
      );
      setNiches(updatedNiches);
      setScreen(Screen.niche_templates);
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const updatePurchaseDetails = async (addOnItemName: AddOnItemName) => {
    const item = addOnItems.find((x) => x.itemName === addOnItemName);
    if (item) {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/${item?.id}/purchase-details`,
        "PATCH",
        {},
        {
          completed: true,
        },
      );
      if (item.itemName === AddOnItemName.niche) {
        await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/organization-niches/notifications`,
          "POST",
          {},
          {
            nicheIds: niches?.map((x) => x.id),
          },
        );
      }
    }
  };

  const saveOrgVaProfile = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/va-profile`,
        "PUT",
        {},
        { ...organizationVaProfile, source: SplashPage.va_bundled },
      );
      setOrganizationVaProfile(res.organizationVaProfile);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const redirectToRightPage = async (
    addOnItems: AddOnItem[],
    niches: OrganizationNiche[],
  ) => {
    const numOfNiche =
      getQuantityForItem(AddOnItemName.niche, addOnItems || []) || 0;
    const numOfTemplates =
      getQuantityForItem(AddOnItemName.pitching_templates, addOnItems) || 0;
    const nicheAddOnItem = addOnItems.find(
      (x: AddOnItem) => x.itemName === AddOnItemName.niche,
    );
    const templateAddOnItem = addOnItems.find(
      (x: AddOnItem) => x.itemName === AddOnItemName.pitching_templates,
    );
    if (numOfNiche > 0 && !nicheAddOnItem?.purchaseDetails?.completed) {
      setScreen(Screen.niche);
      const nichesWithNoTemplates: OrganizationNiche[] = niches?.filter(
        (niche) => !niche.emailSettingId && !niche?.needsTemplate,
      );
      if (nichesWithNoTemplates?.length === numOfNiche) {
        setNiches(nichesWithNoTemplates);
      } else {
        for (let i = 0; i < numOfNiche; i++) {
          nichesWithNoTemplates.push({
            name: `Niche ${i + 1}`,
            notes: "",
            templates: [],
            status: OrganizationNicheStatus.pending,
          });
        }
        setNiches(nichesWithNoTemplates);
      }
    } else if (
      numOfTemplates > 0 &&
      !templateAddOnItem?.purchaseDetails?.completed
    ) {
      setScreen(Screen.template);
    } else {
      setScreen(Screen.completed);
    }
  };

  const getFinalStepText = () => {
    let text = "";
    if (numOfCall > 0) {
      text +=
        "To book your 1:1 support call with Bento, click the button below. ";
    }
    if (numOfTemplates > 0) {
      text += `You will receive an email from us when your custom pitch template${
        numOfTemplates > 1 ? "s are" : " is"
      } ready.`;
    }
    if (numOfCall === 0 && numOfTemplates === 0) {
      text += "You can now go to your dashboard.";
    }
    return text;
  };

  useEffect(() => {
    if (!currentOrg?.id) {
      fetchUserInfo();
    } else if (invoiceId && currentOrg?.id) {
      fetchPurchasedItems();
      getCategoriesToPickFrom();
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, currentOrg?.id]);

  return (
    <SlideAnimationProvider>
      <>
        {loading ? (
          <Grid container sx={styles.root}>
            <Grid item xs={12}>
              <Skeleton animation="wave" width="80%" height="50px" />
              <Skeleton
                animation="wave"
                width="100%"
                height="400px"
                sx={{ mt: -8 }}
              />
              <Skeleton
                animation="wave"
                width="100%"
                height="100px"
                sx={{ mt: -8 }}
              />
              <Skeleton
                animation="wave"
                width="100%"
                height="200px"
                sx={{ mt: -4 }}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container sx={styles.root}>
            <Grid sx={styles.formContainer} item xs={12}>
              {screen === Screen.niche && (
                <NicheForm
                  categoriesToPickFrom={categoriesToPickFrom}
                  handleNext={handleSubmitNiche}
                  niches={niches}
                  setNiches={setNiches}
                  saveLoading={saveLoading}
                  templateIntakeForms={templateIntakeForms}
                />
              )}
              {screen === Screen.niche_templates && (
                <NicheTemplateForm
                  saveOrgVaProfile={saveOrgVaProfile}
                  handleNext={async () => {
                    if (numOfTemplates > 0) {
                      setScreen(Screen.template);
                    } else {
                      setScreen(Screen.completed);
                    }
                    await updatePurchaseDetails(AddOnItemName.niche);
                  }}
                />
              )}
              {screen === Screen.template && templateAddOn?.id && (
                <CopywritersFlow
                  addOnItemId={templateAddOn?.id}
                  handleNext={async () => {
                    setScreen(Screen.completed);
                    await updatePurchaseDetails(
                      AddOnItemName.pitching_templates,
                    );
                  }}
                  showNichePickQuestion={numOfNiche === 0}
                />
              )}

              {screen === Screen.completed && (
                <>
                  <Typography variant="h3" textAlign={"center"}>
                    You have completed all required steps! 🎉
                  </Typography>

                  <Box sx={{ ml: 4, mt: 2 }}>
                    <Typography gutterBottom textAlign={"center"}>
                      {getFinalStepText()}
                    </Typography>
                    <Grid container justifyContent={"center"} sx={{ mt: 2 }}>
                      <Grid container justifyContent={"center"} sx={{ mt: 2 }}>
                        <Button onClick={goToDashboard}>Go To Dashboard</Button>
                        {numOfCall > 0 && (
                          <Button
                            onClick={handleBookCall}
                            target="_blank"
                            href={BENTO_VA_ONBOARDING_CALENDLY_LINK}
                            variant="contained"
                            size="small"
                            disableElevation
                          >
                            Book Call
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </>
    </SlideAnimationProvider>
  );
};

export default VaBundledIntakeForm;
